import React, { useRef } from 'react'
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Button,
  useToast,
  VStack
} from '@chakra-ui/react'
import { Payment, AdminSettings, UserProfile } from '@/types/user'

interface InvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  invoice: Payment | null
  adminSettings?: AdminSettings | null
  currentUser?: UserProfile | null
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({ isOpen, onClose, invoice, adminSettings, currentUser }) => {
  const printRef = useRef<HTMLDivElement>(null)
  const toast = useToast()

  if (!invoice) return null

  // Company details from admin settings or defaults
  const companyName = adminSettings?.tradingName || "Ai Scoring App"
  const companyAddress = adminSettings?.address || "Pinelands, Cape Town, South Africa, 7405"
  const companyEmail = adminSettings?.email || "info@aiscoringapp.com"
  const companyVat = adminSettings?.vat || "4630284075"

  const formatDate = (date: any) => {
    if (!date) return ""
    const d = date.toDate ? date.toDate() : new Date(date)
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).replace(/ /g, "-")
  }

  const invoiceNumber = invoice.invoiceNumber || invoice.paystackTransactionId || "N/A"
  const customerName = currentUser?.displayName || "Customer"
  const customerEmail = currentUser?.email || "customer@example.com"
  const companyNameFromUser = currentUser?.companyName || "Company Name"
  const companyVatFromUser = currentUser?.vatNumber || "VAT Number"
  const planName = "Individual Plan" // This would come from subscription

  const amount = invoice.amount / 100
  const vatPercentage = adminSettings?.vatPercentage || 15
  const amountExcludingVat = vatPercentage ? amount / (1 + vatPercentage / 100) : amount
  const vatAmount = amount - amountExcludingVat

  const handlePrint = () => {
    if (!printRef.current) return

    const printContent = printRef.current.innerHTML
    const originalBody = document.body.innerHTML

    document.body.innerHTML = printContent
    window.print()
    document.body.innerHTML = originalBody
    window.location.reload()

    toast({
      title: "Print initiated",
      description: "Invoice sent to printer",
      status: "success",
      duration: 2000,
      isClosable: true,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Invoice #{invoiceNumber}</ModalHeader>
        <ModalBody>
          <div
            ref={printRef}
            style={{
              padding: 24,
              background: "white",
              color: "black",
              fontFamily: "Arial, sans-serif"
            }}
          >
            <h2 style={{
              fontWeight: "bold",
              fontSize: 24,
              marginBottom: 8
            }}>
              Invoice
            </h2>

            {/* Company Details */}
            <div style={{ marginBottom: 16 }}>
              <div><b>Trading Name:</b> {companyName}</div>
              <div><b>Address:</b> {companyAddress}</div>
              <div><b>Email:</b> {companyEmail}</div>
              <div><b>VAT:</b> {companyVat}</div>
            </div>

            {/* Invoice Details */}
            <div style={{ marginBottom: 16 }}>
              <div><b>Invoice Date:</b> {formatDate(invoice.createdAt)}</div>
              <div><b>Invoice Number:</b> {invoiceNumber}</div>
              <div><b>Reference:</b> {invoice.paystackTransactionId || "N/A"}</div>
              <div><b>Status:</b> {invoice.status}</div>
            </div>

            {/* Customer Details */}
            <div style={{ marginBottom: 16 }}>
              <div><b>Customer Name:</b> {customerName}</div>
              <div><b>Customer Email:</b> {customerEmail}</div>
              {companyNameFromUser && companyNameFromUser !== "Company Name" && (
                <div><b>Company Name:</b> {companyNameFromUser}</div>
              )}
              {companyVatFromUser && companyVatFromUser !== "VAT Number" && (
                <div><b>Company VAT Reg:</b> {companyVatFromUser}</div>
              )}
              {(currentUser?.address?.street || currentUser?.address?.city || currentUser?.address?.postalCode) && (
                <div><b>Address:</b> {[currentUser?.address?.street, currentUser?.address?.city, currentUser?.address?.postalCode, currentUser?.address?.country].filter(Boolean).join(', ')}</div>
              )}
            </div>

            {/* Plan Details */}
            <div style={{ marginBottom: 16 }}>
              <div><b>Plan:</b> {planName}</div>
            </div>

            {/* Amount Details */}
            <div style={{ marginBottom: 16 }}>
              <div><b>Amount Excluding VAT:</b> R{amountExcludingVat.toFixed(2)}</div>
              <div><b>VAT ({vatPercentage}%):</b> R{vatAmount.toFixed(2)}</div>
              <div><b>Total Amount:</b> R{amount.toFixed(2)}</div>
            </div>

            {/* Footer */}
            <div style={{
              marginTop: 32,
              fontSize: 12,
              color: "#666"
            }}>
              <div>Thank you for your payment!</div>
              <div>For support, contact info@AiScoringApp.com</div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <VStack spacing={2} w="full">
            <Button
              colorScheme="blue"
              onClick={handlePrint}
              w="full"
            >
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              w="full"
            >
              Close
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
} 